/* global BigInt */
// Issue-bonus form of the private VIP dashboard: the manager issues a bonus for a player in his
// book into bi_backoffice.temp_vip_bonuses_hist. The USD value is shown while he types, because a
// wrong currency (10 BTC instead of 10 USDT) is easy to miss; above manualApprovalUSD the issuing
// process holds the bonus for manual approval (the app does not block it).
import { NATIVE_FIAT, usdValue } from '../vip/bonus_requests/format';

const AMOUNT_PATTERN = /^\d{1,12}(\.\d{1,8})?$/;
const USD_PATTERN = /^\d{1,12}(\.\d{1,2})?$/;
// an exchange rate from the database: any precision (numeric as text)
const RATE_PATTERN = /^\d+(\.\d+)?$/;
const FIAT_DECIMALS = 2;
const CRYPTO_DECIMALS = 8;
const MAX_TITLE = 200;
const MAX_COMMENT = 1000;
const MAX_WAGERING = 9999.99;
const MAX_DURATION_DAYS = 365;

// initialBonusForm: the form state for a player, preselecting his first account with a USD rate.
export const initialBonusForm = (form) => {
  const issuable = (form?.accounts || []).find((a) => a.usd_rate);
  return {
    currency: issuable?.currency || '',
    amount: '',
    // usd is the USD value; source says which of the two holds what the manager typed (the other
    // is converted); mode is the one he types in (USD first); rounded: in USD mode the converted
    // amount was rounded up to the currency's step (usd keeps what he entered)
    usd: '',
    source: 'usd',
    mode: 'usd',
    rounded: false,
    title: '',
    wagering: String(form?.default_wagering ?? 30),
    durationDays: String(form?.default_duration_days ?? 7),
    comment: '',
  };
};

// accountFor: the player's account in the currency, or undefined.
export const accountFor = (accounts, currency) => (accounts || []).find((a) => a.currency === currency);

// bonusUSD: the amount's USD value at the account's rate ("1234.50"), '' when it can't be computed.
export const bonusUSD = (state, accounts) => {
  const account = accountFor(accounts, state.currency);
  const amount = String(state.amount || '').trim();
  if (!account?.usd_rate || !AMOUNT_PATTERN.test(amount)) return '';
  return usdValue(amount, account.usd_rate);
};

// amountDecimals: how many decimals an amount in the currency is given with (fiat 2, crypto 8).
export const amountDecimals = (currency) => (NATIVE_FIAT.has(currency) ? FIAT_DECIMALS : CRYPTO_DECIMALS);

const decimalParts = (s) => {
  const [int, frac = ''] = s.split('.');
  return { digits: BigInt(int + frac), scale: frac.length };
};

// currencyAmount: usd converted at rate (USD per unit) into an amount with the given decimals,
// half up, without trailing zeros; exact (no floats). '' when either is not a decimal or rate is 0.
export const currencyAmount = (usd, rate, decimals) => {
  const text = String(usd || '').trim();
  if (!USD_PATTERN.test(text) || !RATE_PATTERN.test(String(rate || '').trim())) return '';
  const u = decimalParts(text);
  const r = decimalParts(String(rate).trim());
  if (r.digits === 0n) return '';
  // amount * 10^decimals = u / 10^u.scale / (r / 10^r.scale) * 10^decimals
  const num = u.digits * 10n ** BigInt(r.scale + decimals);
  const den = r.digits * 10n ** BigInt(u.scale);
  const units = (num * 2n + den) / (2n * den);
  const int = units / 10n ** BigInt(decimals);
  const frac = String(units % 10n ** BigInt(decimals)).padStart(decimals, '0').replace(/0+$/, '');
  return frac ? `${int}.${frac}` : String(int);
};

// convertedFrom: the state with the field the manager did not type recomputed from the one he did.
const convertedFrom = (state, accounts) => {
  const rate = accountFor(accounts, state.currency)?.usd_rate;
  if (state.source === 'usd') {
    return { ...state, amount: rate ? currencyAmount(state.usd, rate, amountDecimals(state.currency)) : '' };
  }
  return { ...state, usd: bonusUSD(state, accounts) };
};

// withAmount / withUSD / withCurrency: the state after the manager types an amount, types a USD
// value or picks another account; the other field follows.
export const withAmount = (state, accounts, amount) => convertedFrom({ ...state, amount, source: 'amount', rounded: false }, accounts);
export const withUSD = (state, accounts, usd) => convertedFrom({ ...state, usd, source: 'usd', rounded: false }, accounts);
// another account converts again from the field being typed in (a rounding is dropped)
export const withCurrency = (state, accounts, currency) => convertedFrom(
  { ...state, currency, source: state.mode === 'usd' ? 'usd' : 'amount', rounded: false }, accounts,
);
// withMode: type the amount in the currency ('currency') or in USD ('usd'). The value carries
// over: the amount that would be saved (rounded or not), or its USD value.
export const withMode = (state, accounts, mode) => {
  if (mode === state.mode) return state;
  if (mode === 'usd') {
    return { ...state, mode, source: 'usd', rounded: false, usd: bonusUSD(state, accounts) || state.usd };
  }
  return convertedFrom({ ...state, mode, source: 'amount', rounded: false }, accounts);
};

// roundingFor: the step the currency's amount is rounded up to, '' without a rule.
export const roundingFor = (accounts, currency) => accountFor(accounts, currency)?.rounding || '';

// ceilToStep: amount rounded up to the next multiple of step, without trailing zeros; exact.
// '' when either is not a decimal or the step is 0.
export const ceilToStep = (amount, step) => {
  const a = String(amount || '').trim();
  const st = String(step || '').trim();
  if (!RATE_PATTERN.test(a) || !RATE_PATTERN.test(st)) return '';
  const scale = Math.max(decimalParts(a).scale, decimalParts(st).scale);
  const scaled = (s) => { const p = decimalParts(s); return p.digits * 10n ** BigInt(scale - p.scale); };
  const units = scaled(a);
  const unit = scaled(st);
  if (unit === 0n) return '';
  const steps = (units + unit - 1n) / unit;
  const total = steps * unit;
  const int = total / 10n ** BigInt(scale);
  const frac = scale ? String(total % 10n ** BigInt(scale)).padStart(scale, '0').replace(/0+$/, '') : '';
  return frac ? `${int}.${frac}` : String(int);
};

// roundedAmount: in USD mode, the converted amount rounded up to the currency's step (the comps'
// rule), '' when there is no step, no amount, or it already is a multiple of the step.
export const roundedAmount = (state, accounts) => {
  if (state.mode !== 'usd' || state.rounded) return '';
  const rounded = ceilToStep(state.amount, roundingFor(accounts, state.currency));
  return rounded && rounded !== state.amount ? rounded : '';
};

// applyRounding / undoRounding: save the rounded amount instead of the plain conversion, or go
// back to it. usd keeps what the manager entered; bonusUSD gives what the player receives.
export const applyRounding = (state, accounts) => {
  const rounded = roundedAmount(state, accounts);
  return rounded ? { ...state, amount: rounded, rounded: true } : state;
};
export const undoRounding = (state, accounts) => convertedFrom({ ...state, rounded: false }, accounts);

// needsManualApproval: the USD value is above the issuing process's manual-approval threshold.
export const needsManualApproval = (usd, threshold) => usd !== '' && threshold > 0 && Number(usd) > threshold;

// bonusFormError: what keeps the form from being sent, '' when it can be.
export const bonusFormError = (state, accounts) => {
  const title = state.title.trim();
  const amount = String(state.amount).trim();
  const wagering = Number(state.wagering);
  const days = Number(state.durationDays);
  const account = accountFor(accounts, state.currency);
  if (!account) return 'Pick one of the player\'s accounts.';
  if (!account.usd_rate) return `No USD rate for ${state.currency} today: a bonus in it cannot be issued.`;
  if (state.source === 'usd' && !USD_PATTERN.test(String(state.usd).trim())) return 'USD value must be a number with at most 2 decimals.';
  if (!AMOUNT_PATTERN.test(amount) || Number(amount) <= 0) return 'Amount must be a positive number with at most 8 decimals.';
  if (!title) return 'Title is required.';
  if (title.length > MAX_TITLE) return `Title must be at most ${MAX_TITLE} characters.`;
  if (state.wagering === '' || !Number.isFinite(wagering) || wagering < 0 || wagering > MAX_WAGERING) return `Wagering must be between 0 and ${MAX_WAGERING}.`;
  if (!Number.isInteger(days) || days < 1 || days > MAX_DURATION_DAYS) return `Duration must be 1 to ${MAX_DURATION_DAYS} days.`;
  if (state.comment.trim().length > MAX_COMMENT) return `Comment must be at most ${MAX_COMMENT} characters.`;
  return '';
};

// bonusPayload: the POST /api/vip-private/bonuses body.
export const bonusPayload = (userId, state) => ({
  user_id: userId,
  title: state.title.trim(),
  currency: state.currency,
  amount: String(state.amount).trim(),
  wagering: Number(state.wagering),
  duration_days: Number(state.durationDays),
  comment: state.comment.trim(),
});
